'use client';

import { MouseEvent } from 'react';
import Image from 'next/image';
import {
  ArrowUpRightIcon,
  ArrowUpLeftIcon,
  ArrowDownLeftIcon,
  ArrowDownRightIcon,
} from '@heroicons/react/20/solid';

import {
  ButtonV1,
  FingerTouchGesturesAnimation,
} from '@northladder/design-system';
import { useTranslations } from '@northladder/i18n';

interface ITouchScreenCanvasPromptProps {
  onPress: () => void;
  onPressBack: () => void;
}

/**
 * ----------------------------------------------------------------------------
 * This component will render a canvas to test whether the user's touch is
 * working, and will display an overlay over an image which the user has to
 * scribe off within a give count down timer.
 *
 * TODO: Fix the arrow indicator icons that are off-canvas on mobile.
 */
export function TouchScreenCanvasPrompt({
  onPress,
  onPressBack,
}: ITouchScreenCanvasPromptProps) {
  const t = useTranslations('TouchScreenInit');

  /**
   * The URL of the image. This will be most likely generated from the app config.
   */
  const TARGET_BG_IMAGE_SRC =
    '/images/assessment-images/touch-screen/touch-screen-test-target-pattern-bg.png';

  const handleBackToPrompt = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onPressBack();
  };

  return (
    <div className="relative z-50 h-screen w-full items-center justify-center bg-black">
      {/* eslint-disable */}
      <div className="relative h-full w-full" onClick={onPress} role="button">
        {/* eslint-disable */}
        <Image
          alt="Front white Screen"
          className="absolute h-full w-full object-cover"
          fill
          priority
          src={TARGET_BG_IMAGE_SRC}
        />

        <div
          className="
            relative flex h-full w-full flex-col items-center justify-center
            gap-24  bg-stone-100 opacity-95
          "
        >
          <div
            className="
              bg-primary-300 relative flex h-52 w-52 items-center justify-center
              overflow-hidden rounded-full border-8 border-white
            "
          >
            <Image
              alt="Front white Screen"
              className="h-auto w-auto pt-20"
              height={300}
              priority
              src="/images/assessment-images/FRONT_WHITE.png"
              width={160}
            />
            <FingerTouchGesturesAnimation className="absolute bottom-0 h-32 w-32 rounded-full mix-blend-multiply " />
          </div>

          <div className="flex max-w-xs flex-col items-center justify-center gap-2">
            <h1 className="text-center text-2xl font-bold leading-8 text-gray-800">
              {t('title')}
            </h1>
            <p className="text-center text-base font-medium leading-tight text-stone-900">
              {t('swipeScreen')}
            </p>

            <ButtonV1
              className="z-50 mx-auto mt-2 w-auto"
              label={t('beginCTA')}
              onClick={onPress}
              type="button"
            />

            <button
              className="z-50 p-4 text-sm text-gray-400"
              id="back-to-prompt-button"
              onClick={handleBackToPrompt}
              type="button"
            >
              {t('backCTAText')}
            </button>
          </div>

          <div className="absolute left-0 right-0 top-0 z-50 flex w-full justify-between p-2 opacity-50">
            <ArrowUpLeftIcon className="h-6 w-6" />
            <ArrowUpRightIcon className="h-6 w-6" />
          </div>

          <div className="absolute bottom-0 left-0 right-0 z-50 flex w-full justify-between p-2 opacity-50">
            <ArrowDownLeftIcon className="h-6 w-6" />
            <ArrowDownRightIcon className="h-6 w-6" />
          </div>
        </div>
      </div>
      <div className="base-grad pointer-events-none absolute left-0 top-0 h-full w-full" />
    </div>
  );
}
